import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Download, Filter, BarChart3, RefreshCcw, AlertTriangle, Route as RouteIcon } from "lucide-react";
import { Link } from "react-router-dom";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from "recharts";

const PIE_COLORS = ["#0ea5e9", "#f97316", "#10b981", "#a855f7", "#f59e0b", "#ef4444", "#3b82f6"];

function startOfMonthIso() {
  const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}
function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function FilterBar({ filters, setFilters, paymentTypes, paidMethods, staff, customers, onApply, busy }) {
  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  return (
    <div className="bg-card border border-border rounded-[8px] p-4" data-testid="reports-filterbar">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">From <span className="text-accent">*</span></Label>
          <Input type="date" value={filters.from} onChange={(e) => setF("from", e.target.value)} data-testid="reports-from" className="h-9" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">To <span className="text-accent">*</span></Label>
          <Input type="date" value={filters.to} onChange={(e) => setF("to", e.target.value)} data-testid="reports-to" className="h-9" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Granularity</Label>
          <Select value={filters.granularity} onValueChange={(v) => setF("granularity", v)}>
            <SelectTrigger className="h-9" data-testid="reports-granularity"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="day" data-testid="reports-granularity-day">Day</SelectItem>
              <SelectItem value="week" data-testid="reports-granularity-week">Week</SelectItem>
              <SelectItem value="month" data-testid="reports-granularity-month">Month</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Payment type</Label>
          <Select value={filters.payment_type} onValueChange={(v) => setF("payment_type", v)}>
            <SelectTrigger className="h-9" data-testid="reports-payment-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="reports-payment-type-all">All</SelectItem>
              {paymentTypes.map((p) => <SelectItem key={p.code} value={p.code} data-testid={`reports-payment-type-${p.code}`}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Payment method</Label>
          <Select value={filters.payment_method} onValueChange={(v) => setF("payment_method", v)}>
            <SelectTrigger className="h-9" data-testid="reports-payment-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="reports-payment-method-all">All</SelectItem>
              {paidMethods.map((p) => <SelectItem key={p.code} value={p.code} data-testid={`reports-payment-method-${p.code}`}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Staff</Label>
          <Select value={filters.staff_id} onValueChange={(v) => setF("staff_id", v)}>
            <SelectTrigger className="h-9" data-testid="reports-staff"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="reports-staff-all">All</SelectItem>
              {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2 lg:col-span-2">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Customer</Label>
          <Select value={filters.customer_id} onValueChange={(v) => setF("customer_id", v)}>
            <SelectTrigger className="h-9" data-testid="reports-customer"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="reports-customer-all">All</SelectItem>
              {customers.slice(0, 200).map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2 lg:col-span-4 flex items-end justify-end">
          <Button onClick={onApply} disabled={busy} className="btn-cta h-9" data-testid="reports-apply">
            <RefreshCcw className="h-3.5 w-3.5 mr-1" /> {busy ? "Loading…" : "Apply filters"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="bg-card border border-border rounded-[8px] p-4" data-testid={testid}>
      <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className="text-2xl font-extrabold mt-1">{value}</div>
    </div>
  );
}

export default function Reports() {
  const [filters, setFilters] = useState({
    from: startOfMonthIso(),
    to: todayIso(),
    granularity: "day",
    payment_type: "all",
    payment_method: "all",
    staff_id: "all",
    customer_id: "all",
  });
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [paidMethods, setPaidMethods] = useState([]);
  const [staff, setStaff] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [currency, setCurrency] = useState("EUR");
  const [period, setPeriod] = useState(null);
  const [byMethod, setByMethod] = useState(null);
  const [byType, setByType] = useState(null);
  const [aged, setAged] = useState(null);
  const [onlinePayments, setOnlinePayments] = useState(null); // Phase 27
  const [staffSettled, setStaffSettled] = useState(null);     // Phase 28
  const [atRiskCount, setAtRiskCount] = useState(null);       // Phase 34
  const [mileage, setMileage] = useState(null);               // Phase 43
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/settings/payment-types").catch(() => ({ data: [] })),
      api.get("/settings/paid-methods").catch(() => ({ data: [] })),
      api.get("/staff").catch(() => ({ data: [] })),
      api.get("/customers").catch(() => ({ data: [] })),
      api.get("/settings").catch(() => ({ data: { currency: "EUR" } })),
    ]).then(([pt, pm, s, c, set]) => {
      setPaymentTypes(pt.data || []);
      setPaidMethods(pm.data || []);
      setStaff(s.data || []);
      setCustomers(c.data || []);
      setCurrency(set.data?.currency || "EUR");
    });
  }, []);

  const queryParams = useMemo(() => {
    const q = { from: filters.from ? `${filters.from}T00:00:00Z` : "", to: filters.to ? `${filters.to}T23:59:59Z` : "" };
    if (filters.payment_type !== "all") q.payment_type = filters.payment_type;
    if (filters.payment_method !== "all") q.payment_method = filters.payment_method;
    if (filters.staff_id !== "all") q.staff_id = filters.staff_id;
    if (filters.customer_id !== "all") q.customer_id = filters.customer_id;
    return q;
  }, [filters]);

  const load = async () => {
    if (!filters.from || !filters.to) {
      toast.error("Pick a date range");
      return;
    }
    setBusy(true);
    try {
      const [p, m, t, a, op, ss, ar, ml] = await Promise.all([
        api.get("/reports/revenue-by-period", { params: { ...queryParams, granularity: filters.granularity } }),
        api.get("/reports/revenue-by-payment-method", { params: queryParams }),
        api.get("/reports/revenue-by-payment-type", { params: queryParams }),
        api.get("/reports/aged-debt", { params: { as_of: queryParams.to, payment_type: queryParams.payment_type, customer_id: queryParams.customer_id } }),
        api.get("/reports/online-payments-summary", { params: { days: 30 } }).catch(() => ({ data: null })),
        api.get("/reports/staff-cash-settled-summary", { params: { days: 30 } }).catch(() => ({ data: null })),
        api.get("/customers/at-risk").catch(() => ({ data: { count: 0, items: [] } })),
        // Phase 43 — driver mileage tile (fixed last 30 days; the dedicated
        // /reports/mileage page is responsible for any future date-range
        // picker). Failure is non-fatal — older companies have no staff
        // base addresses geocoded and the endpoint returns empty rollups.
        api.get("/reports/mileage", {
          params: {
            from: (() => { const d = new Date(); d.setDate(d.getDate() - 29); return d.toISOString().slice(0, 10); })(),
            to: todayIso(),
          },
        }).catch(() => ({ data: { rows: [], rollups: [] } })),
      ]);
      setPeriod(p.data);
      setByMethod(m.data);
      setByType(t.data);
      setAged(a.data);
      setOnlinePayments(op.data);
      setStaffSettled(ss.data);
      setAtRiskCount(ar.data?.count ?? (Array.isArray(ar.data?.items) ? ar.data.items.length : 0));
      setMileage(ml.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadCsv = async (path, name) => {
    try {
      const res = await api.get(path, { params: { ...queryParams, granularity: filters.granularity }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="page space-y-5" data-testid="reports-page">
      <div>
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-accent">// Insights</div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
          <BarChart3 className="h-8 w-8 text-accent" /> Reports
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Revenue, mix, and ageing — filtered to the date range below.</p>
      </div>

      <FilterBar
        filters={filters}
        setFilters={setFilters}
        paymentTypes={paymentTypes}
        paidMethods={paidMethods}
        staff={staff}
        customers={customers}
        onApply={load}
        busy={busy}
      />

      {/* Phase 43 — Driver mileage tile (last 30 days, click-through to /reports/mileage) */}
      {mileage && (
        <Link
          to="/reports/mileage"
          className="block bg-card border border-border rounded-[10px] p-5 hover:border-accent transition-colors"
          data-testid="reports-mileage-tile"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-amber-100 dark:bg-amber-900/40 border border-amber-200 dark:border-amber-800 flex items-center justify-center">
                <RouteIcon className="h-5 w-5 text-amber-700 dark:text-amber-300" />
              </div>
              <div>
                <div className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  Driver mileage · last 30 days
                </div>
                <div className="text-2xl font-black tabular-nums mt-0.5" data-testid="reports-mileage-total-km">
                  {(mileage.rollups || []).reduce((s, r) => s + Number(r.total_km || 0), 0).toFixed(1)} km
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  Across <b data-testid="reports-mileage-drivers-count">
                    {(mileage.rollups || []).filter((r) => Number(r.total_km || 0) > 0).length}
                  </b> driver(s) · {(mileage.rollups || []).reduce((s, r) => s + Number(r.days_worked || 0), 0)} days worked
                </div>
              </div>
            </div>
            <div className="text-[11px] mono uppercase tracking-wider text-accent">View detail →</div>
          </div>
          {(mileage.rollups || []).length > 0 && (
            <div className="mt-4 pt-4 border-t border-border grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {mileage.rollups.slice(0, 8).map((r) => (
                <div key={r.staff_id} className="text-sm" data-testid={`reports-mileage-driver-${r.staff_id}`}>
                  <div className="mono text-[10px] uppercase tracking-wider text-muted-foreground truncate">
                    {r.staff_name}
                    {!r.has_base && <span className="ml-1 text-amber-700">· no base</span>}
                  </div>
                  <div className="font-bold tabular-nums mt-0.5">{Number(r.total_km).toFixed(1)} km</div>
                  <div className="text-[11px] text-muted-foreground">
                    {r.days_worked}d · avg {Number(r.avg_km_per_day || 0).toFixed(1)} km/d
                  </div>
                </div>
              ))}
            </div>
          )}
        </Link>
      )}

      {/* Phase 34 — At-risk customers tile */}
      {atRiskCount !== null && (
        <Link
          to="/reports/at-risk-customers"
          className={
            "block bg-card border rounded-[10px] p-5 transition-colors " +
            (atRiskCount > 0
              ? "border-red-300 hover:border-red-500"
              : "border-border hover:border-accent")
          }
          data-testid="reports-at-risk-tile"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div
                className={
                  "h-10 w-10 rounded-full flex items-center justify-center border " +
                  (atRiskCount > 0
                    ? "bg-red-100 border-red-200 dark:bg-red-950/40 dark:border-red-900"
                    : "bg-emerald-100 border-emerald-200 dark:bg-emerald-950/40 dark:border-emerald-900")
                }
              >
                <AlertTriangle
                  className={
                    "h-5 w-5 " +
                    (atRiskCount > 0
                      ? "text-red-600 dark:text-red-300"
                      : "text-emerald-600 dark:text-emerald-300")
                  }
                />
              </div>
              <div>
                <div className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  At-risk customers
                </div>
                <div
                  className="text-2xl font-black tabular-nums mt-0.5"
                  data-testid="reports-at-risk-count"
                >
                  {atRiskCount}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  {atRiskCount > 0
                    ? "Overdue invoices and/or dropped booking frequency. Click to triage."
                    : "Nobody currently meets the at-risk criteria."}
                </div>
              </div>
            </div>
            <div className="text-[11px] mono uppercase tracking-wider text-accent">View →</div>
          </div>
        </Link>
      )}

      {/* Phase 27 — Online payments collected (rolling 30d, audit-mode aware) */}
      {onlinePayments && (
        <section
          className="bg-card border border-border rounded-[10px] p-5 flex flex-wrap items-center justify-between gap-4"
          data-testid="reports-online-payments-tile"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-sky-100 dark:bg-sky-900/40 border border-sky-200 dark:border-sky-800 flex items-center justify-center">
              <BarChart3 className="h-5 w-5 text-sky-600 dark:text-sky-300" />
            </div>
            <div>
              <div className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Online payments collected · last {onlinePayments.window_days}d</div>
              <div className="text-2xl font-black tabular-nums mt-0.5" data-testid="reports-online-payments-total">
                {formatCurrency(onlinePayments.total, onlinePayments.currency)}
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                Across <b data-testid="reports-online-payments-count">{onlinePayments.count}</b> invoice{onlinePayments.count === 1 ? "" : "s"} — paid via Stripe online checkout
              </div>
            </div>
          </div>
          {onlinePayments.count > 0 && (
            <div className="text-[11px] mono text-muted-foreground max-w-md text-right">
              Latest:&nbsp;
              <b className="text-foreground">{onlinePayments.invoices[0]?.invoice_number}</b>
              &nbsp;· {onlinePayments.invoices[0]?.customer_name || "—"}
            </div>
          )}
        </section>
      )}

      {/* Phase 28 — Staff cash settled (rolling 30d, breakdown by method) */}
      {staffSettled && (
        <section
          className="bg-card border border-border rounded-[10px] p-5"
          data-testid="reports-staff-settled-tile"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-emerald-100 dark:bg-emerald-900/40 border border-emerald-200 dark:border-emerald-800 flex items-center justify-center">
                <BarChart3 className="h-5 w-5 text-emerald-600 dark:text-emerald-300" />
              </div>
              <div>
                <div className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Staff cash settled · last {staffSettled.window_days}d</div>
                <div className="text-2xl font-black tabular-nums mt-0.5" data-testid="reports-staff-settled-total">
                  {formatCurrency(staffSettled.total, staffSettled.currency)}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  Across <b data-testid="reports-staff-settled-count">{staffSettled.count}</b> settlement{staffSettled.count === 1 ? "" : "s"}
                </div>
              </div>
            </div>
          </div>
          {staffSettled.count > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-border">
              {Object.entries(staffSettled.by_method || {}).map(([code, bucket]) => (
                <div key={code} className="text-sm" data-testid={`reports-staff-settled-method-${code}`}>
                  <div className="mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    {code.replace(/_/g, " ")}
                  </div>
                  <div className="font-bold tabular-nums mt-0.5">{formatCurrency(bucket.total || 0, staffSettled.currency)}</div>
                  <div className="text-[11px] text-muted-foreground">{bucket.count || 0} event{(bucket.count || 0) === 1 ? "" : "s"}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <Tabs defaultValue="revenue" className="space-y-4">
        <TabsList data-testid="reports-tabs">
          <TabsTrigger value="revenue" data-testid="reports-tab-revenue">Revenue by period</TabsTrigger>
          <TabsTrigger value="method" data-testid="reports-tab-method">By payment method</TabsTrigger>
          <TabsTrigger value="type" data-testid="reports-tab-type">By payment type</TabsTrigger>
          <TabsTrigger value="aged" data-testid="reports-tab-aged">Aged debt</TabsTrigger>
          <TabsTrigger value="staff_ledger" data-testid="reports-tab-staff-ledger">Staff ledger</TabsTrigger>
        </TabsList>

        <TabsContent value="revenue">
          <RevenueByPeriod data={period} currency={currency} onExport={() => downloadCsv("/reports/revenue-by-period/export", "revenue-by-period.csv")} />
        </TabsContent>
        <TabsContent value="method">
          <RevenueByMethod data={byMethod} currency={currency} onExport={() => downloadCsv("/reports/revenue-by-payment-method/export", "revenue-by-payment-method.csv")} />
        </TabsContent>
        <TabsContent value="type">
          <RevenueByType data={byType} currency={currency} onExport={() => downloadCsv("/reports/revenue-by-payment-type/export", "revenue-by-payment-type.csv")} />
        </TabsContent>
        <TabsContent value="aged">
          <AgedDebt data={aged} currency={currency} onExport={() => downloadCsv("/reports/aged-debt/export", "aged-debt.csv")} />
        </TabsContent>
        <TabsContent value="staff_ledger">
          <StaffLedgerReport filters={filters} currency={currency} onExport={() => downloadCsv("/reports/staff-ledger/export", "staff-ledger.csv")} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ReportShell({ children, onExport, testid }) {
  return (
    <div className="space-y-4" data-testid={testid}>
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={onExport} data-testid={`${testid}-export`}>
          <Download className="h-3 w-3 mr-1" /> Export CSV
        </Button>
      </div>
      {children}
    </div>
  );
}

function RevenueByPeriod({ data, currency, onExport }) {
  if (!data) return <div className="text-sm text-muted-foreground">No data.</div>;
  return (
    <ReportShell onExport={onExport} testid="report-revenue-by-period">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Invoices" value={data.total_count} testid="kpi-invoice-count" />
        <Kpi label="Revenue" value={formatCurrency(data.total_grand, currency)} testid="kpi-revenue" />
        <Kpi label="Subtotal" value={formatCurrency(data.total_subtotal, currency)} testid="kpi-subtotal" />
        <Kpi label="Tax" value={formatCurrency(data.total_tax, currency)} testid="kpi-tax" />
      </div>
      <div className="bg-card border border-border rounded-[8px] p-4">
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.buckets}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="period_label" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${currency === "EUR" ? "€" : currency} ${v}`} />
              <Tooltip formatter={(v) => formatCurrency(v, currency)} />
              <Legend />
              <Line type="monotone" dataKey="total" name="Total" stroke="#0ea5e9" strokeWidth={2} />
              <Line type="monotone" dataKey="tax" name="Tax" stroke="#f97316" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="bg-card border border-border rounded-[8px] overflow-x-auto">
        <table className="w-full text-sm" data-testid="report-revenue-by-period-table">
          <thead className="bg-secondary/40">
            <tr><th className="text-left px-3 py-2">Period</th><th className="text-right px-3 py-2">#</th><th className="text-right px-3 py-2">Subtotal</th><th className="text-right px-3 py-2">Tax</th><th className="text-right px-3 py-2">Total</th></tr>
          </thead>
          <tbody>
            {data.buckets.map((b) => (
              <tr key={b.period_label} className="border-t border-border">
                <td className="px-3 py-2 mono">{b.period_label}</td>
                <td className="px-3 py-2 text-right mono">{b.count}</td>
                <td className="px-3 py-2 text-right mono">{formatCurrency(b.subtotal, currency)}</td>
                <td className="px-3 py-2 text-right mono">{formatCurrency(b.tax, currency)}</td>
                <td className="px-3 py-2 text-right mono font-bold">{formatCurrency(b.total, currency)}</td>
              </tr>
            ))}
            {data.buckets.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">No invoices in range.</td></tr>}
          </tbody>
        </table>
      </div>
    </ReportShell>
  );
}

function RevenueByMethod({ data, currency, onExport }) {
  if (!data) return <div className="text-sm text-muted-foreground">No data.</div>;
  const total = (data.rows || []).reduce((s, r) => s + Number(r.total || 0), 0);
  return (
    <ReportShell onExport={onExport} testid="report-revenue-by-method">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-[8px] p-4">
          <div className="h-72">
            {data.rows.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.rows} dataKey="total" nameKey="paid_method_name" cx="50%" cy="50%" outerRadius={90} label>
                    {data.rows.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => formatCurrency(v, currency)} />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="text-sm text-muted-foreground p-4">No paid invoices in range.</div>}
          </div>
        </div>
        <div className="bg-card border border-border rounded-[8px] overflow-x-auto">
          <table className="w-full text-sm" data-testid="report-revenue-by-method-table">
            <thead className="bg-secondary/40">
              <tr><th className="text-left px-3 py-2">Method</th><th className="text-right px-3 py-2">#</th><th className="text-right px-3 py-2">Total</th><th className="text-right px-3 py-2">Share</th></tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.paid_method_code} className="border-t border-border">
                  <td className="px-3 py-2">{r.paid_method_name}</td>
                  <td className="px-3 py-2 text-right mono">{r.count}</td>
                  <td className="px-3 py-2 text-right mono font-bold">{formatCurrency(r.total, currency)}</td>
                  <td className="px-3 py-2 text-right mono">{total ? `${((r.total / total) * 100).toFixed(1)}%` : "—"}</td>
                </tr>
              ))}
              {data.rows.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">No paid invoices in range.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </ReportShell>
  );
}

function RevenueByType({ data, currency, onExport }) {
  if (!data) return <div className="text-sm text-muted-foreground">No data.</div>;
  return (
    <ReportShell onExport={onExport} testid="report-revenue-by-type">
      <div className="bg-card border border-border rounded-[8px] p-4">
        <div className="h-72">
          {data.rows.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.rows}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="payment_type_name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${currency === "EUR" ? "€" : currency} ${v}`} />
                <Tooltip formatter={(v) => formatCurrency(v, currency)} />
                <Legend />
                <Bar dataKey="total" name="Total" fill="#0ea5e9" />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="text-sm text-muted-foreground p-4">No invoices in range.</div>}
        </div>
      </div>
      <div className="bg-card border border-border rounded-[8px] overflow-x-auto">
        <table className="w-full text-sm" data-testid="report-revenue-by-type-table">
          <thead className="bg-secondary/40">
            <tr><th className="text-left px-3 py-2">Type</th><th className="text-right px-3 py-2">#</th><th className="text-right px-3 py-2">Subtotal</th><th className="text-right px-3 py-2">Tax</th><th className="text-right px-3 py-2">Total</th></tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.payment_type_code} className="border-t border-border">
                <td className="px-3 py-2">{r.payment_type_name}</td>
                <td className="px-3 py-2 text-right mono">{r.count}</td>
                <td className="px-3 py-2 text-right mono">{formatCurrency(r.subtotal, currency)}</td>
                <td className="px-3 py-2 text-right mono">{formatCurrency(r.tax, currency)}</td>
                <td className="px-3 py-2 text-right mono font-bold">{formatCurrency(r.total, currency)}</td>
              </tr>
            ))}
            {data.rows.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">No invoices in range.</td></tr>}
          </tbody>
        </table>
      </div>
    </ReportShell>
  );
}

function AgedDebt({ data, currency, onExport }) {
  if (!data) return <div className="text-sm text-muted-foreground">No data.</div>;
  return (
    <ReportShell onExport={onExport} testid="report-aged-debt">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {data.buckets.map((b) => (
          <Kpi key={b.bucket} label={`${b.bucket} days`} value={`${formatCurrency(b.total, currency)} · ${b.count}`} testid={`aged-bucket-${b.bucket}`} />
        ))}
      </div>
      <div className="bg-card border border-border rounded-[8px] p-4">
        <div className="h-60">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.buckets}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="bucket" />
              <YAxis tickFormatter={(v) => `${currency === "EUR" ? "€" : currency} ${v}`} />
              <Tooltip formatter={(v) => formatCurrency(v, currency)} />
              <Bar dataKey="total" name="Outstanding" fill="#ef4444" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="bg-card border border-border rounded-[8px] overflow-x-auto">
        <table className="w-full text-sm" data-testid="report-aged-debt-table">
          <thead className="bg-secondary/40">
            <tr>
              <th className="text-left px-3 py-2">Invoice</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-right px-3 py-2">Issued</th>
              <th className="text-right px-3 py-2">Days</th>
              <th className="text-left px-3 py-2">Bucket</th>
              <th className="text-right px-3 py-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.invoice_id} className="border-t border-border">
                <td className="px-3 py-2 mono font-bold">{r.invoice_number}</td>
                <td className="px-3 py-2 truncate">{r.customer_name}</td>
                <td className="px-3 py-2 text-right mono text-xs">{(r.issued_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2 text-right mono">{r.days_overdue}</td>
                <td className="px-3 py-2 mono text-[11px]">{r.bucket}</td>
                <td className="px-3 py-2 text-right mono font-bold">{formatCurrency(r.total, currency)}</td>
              </tr>
            ))}
            {data.rows.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">No outstanding invoices.</td></tr>}
          </tbody>
        </table>
      </div>
    </ReportShell>
  );
}

// Phase 13: per-staff cash ledger report
function StaffLedgerReport({ filters, currency, onExport }) {
  const [data, setData] = useState({ rows: [], totals: {}, net_total: 0 });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setBusy(true);
    const params = {};
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    api.get("/reports/staff-ledger", { params })
      .then(({ data: d }) => setData(d))
      .catch(() => {})
      .finally(() => setBusy(false));
  }, [filters.from, filters.to]);

  if (busy && data.rows.length === 0) return <ReportShell testid="report-staff-ledger" onExport={onExport}><div className="text-sm text-muted-foreground">Loading…</div></ReportShell>;

  const types = ["cash_intake", "expense", "payout", "cash_collected_from_staff"];
  const typeLabel = { cash_intake: "Intake", expense: "Expense", payout: "Payout", cash_collected_from_staff: "Returned" };
  const chartData = data.rows.map((r) => ({ name: r.staff_name, net: r.net_amount_owed_to_staff }));

  return (
    <ReportShell testid="report-staff-ledger" onExport={onExport}>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {types.map((t) => (
          <div key={t} className="bg-card border rounded-[8px] p-3" data-testid={`report-staff-ledger-total-${t}`}>
            <div className="mono text-[10px] uppercase tracking-wider text-muted-foreground">{typeLabel[t]}</div>
            <div className="text-xl font-extrabold mt-0.5">{formatCurrency(data.totals?.[t] || 0, currency)}</div>
          </div>
        ))}
        <div className={`rounded-[8px] p-3 border-2 ${data.net_total > 0 ? "border-emerald-300 bg-emerald-50" : data.net_total < 0 ? "border-rose-300 bg-rose-50" : "border-border bg-card"}`}
             data-testid="report-staff-ledger-net-total">
          <div className="mono text-[10px] uppercase tracking-wider text-muted-foreground">Net (all staff)</div>
          <div className={`text-xl font-extrabold mt-0.5 ${data.net_total > 0 ? "text-emerald-800" : data.net_total < 0 ? "text-rose-800" : ""}`}>
            {formatCurrency(data.net_total || 0, currency)}
          </div>
        </div>
      </div>

      <div className="bg-card border rounded-[8px] p-4">
        <div className="mono text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Net position by staff</div>
        <ResponsiveContainer width="100%" height={Math.max(180, 40 * data.rows.length + 40)}>
          <BarChart data={chartData} layout="vertical" margin={{ top: 10, right: 30, left: 60, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" tickFormatter={(v) => formatCurrency(v, currency)} />
            <YAxis type="category" dataKey="name" />
            <Tooltip formatter={(v) => formatCurrency(v, currency)} />
            <Bar dataKey="net" fill="#0ea5e9" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-card border rounded-[8px] overflow-x-auto">
        <table className="w-full text-sm" data-testid="report-staff-ledger-table">
          <thead className="bg-muted/40 text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2">Staff</th>
              <th className="text-right px-3 py-2">Intake</th>
              <th className="text-right px-3 py-2">Expense</th>
              <th className="text-right px-3 py-2">Payout</th>
              <th className="text-right px-3 py-2">Returned</th>
              <th className="text-right px-3 py-2 mono uppercase">Net (signed)</th>
              <th className="text-right px-3 py-2"># entries</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.staff_id} className="border-t" data-testid={`report-staff-ledger-row-${r.staff_id}`}>
                <td className="px-3 py-2 font-bold">{r.staff_name}</td>
                {types.map((t) => (
                  <td key={t} className="px-3 py-2 text-right mono">{formatCurrency(r.subtotals?.[t] || 0, currency)}</td>
                ))}
                <td className={`px-3 py-2 text-right mono font-bold ${r.net_amount_owed_to_staff > 0 ? "text-emerald-700" : r.net_amount_owed_to_staff < 0 ? "text-rose-700" : ""}`}>
                  {r.net_amount_owed_to_staff > 0 ? "+" : ""}{formatCurrency(r.net_amount_owed_to_staff, currency)}
                </td>
                <td className="px-3 py-2 text-right mono">{r.entry_count}</td>
              </tr>
            ))}
            {data.rows.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">No staff data.</td></tr>}
          </tbody>
        </table>
      </div>
    </ReportShell>
  );
}
